import { MapPin } from 'lucide-react'

import { US_STATES } from '@snootsvet/shared'
import { memberAddressSchema } from '@snootsvet/shared/portal'

import {
  EditActions,
  EditableField,
  EditableSubfield,
  EditableValue,
  EditButton,
  useEditableField,
} from '@/customer/components/editable-field'
import { Input } from '@/customer/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/customer/components/ui/select'
import { useGetMe } from '@/customer/hooks/use-get-me'
import { useRegion } from '@/customer/hooks/use-region'
import { useUpdateMe } from '@/customer/hooks/use-update-me'

interface AddressDraft {
  address1: string
  address2: string
  city: string
  /**
   * USPS 2-letter code for US members; always `null` for UK members.
   * Present in the draft (rather than derived at save time) so the
   * value round-trips cleanly through `useEditableField`'s generic
   * state and matches the required-but-nullable schema field.
   */
  state: string | null
  postalCode: string
  country: string
}

const STATE_OPTIONS = US_STATES.map((s) => ({ value: s.code, label: s.name }))

/**
 * Portal address field.
 *
 * The one identifier on the profile page that's DB-only - auth
 * doesn't store postal addresses, so this reads from the `/portal/me`
 * snapshot and writes via `PATCH /portal/me` with no verification dance.
 *
 * Address is treated atomically: the required lines (line 1, city,
 * postcode, country) are validated client-side before save. We never
 * write a partial address, so the flat address columns on `members`
 * never diverge across successive edits.
 */
export function AddressProfileField() {
  const me = useGetMe()
  const region = useRegion()
  const updateMe = useUpdateMe()

  const member = me.data?.member
  const isUs = member?.region === 'us'
  const postalCodeLabel = region.postalCodeLabel
  const initial: AddressDraft = {
    address1: member?.address1 ?? '',
    address2: member?.address2 ?? '',
    city: member?.city ?? '',
    state: member?.state ?? null,
    postalCode: member?.postalCode ?? '',
    country: member?.country ?? '',
  }

  const addressField = useEditableField({
    initialValue: initial,
    onSave: async ({ value, setError }) => {
      if (isUs && !value.state) {
        return setError('Please select your state.')
      }
      const parsed = memberAddressSchema.safeParse(value)
      if (!parsed.success) {
        return setError(
          `Please fill in address line 1, city, ${postalCodeLabel.toLowerCase()}, and country.`
        )
      }
      try {
        await updateMe.mutateAsync({ address: parsed.data })
      } catch {
        setError('Something went wrong saving your address. Please try again.')
      }
    },
  })

  if (!member) return null

  const displayValue = formatAddress(member, isUs) ?? (
    <span className="text-muted-foreground">Not set</span>
  )

  return (
    <EditableField
      label="Home address"
      icon={MapPin}
      error={addressField.error}
      action={<EditButton field={addressField} label="home address" />}
    >
      {addressField.isEditing ? (
        <div className="flex flex-col gap-3">
          <EditableSubfield label="Line 1">
            <Input
              autoFocus
              variant="underline"
              value={addressField.draft.address1}
              onChange={(e) =>
                addressField.setDraft({ ...addressField.draft, address1: e.target.value })
              }
              placeholder="Address Line 1"
              autoComplete="address-line1"
            />
          </EditableSubfield>
          <EditableSubfield label="Line 2 (optional)">
            <Input
              variant="underline"
              value={addressField.draft.address2}
              onChange={(e) =>
                addressField.setDraft({ ...addressField.draft, address2: e.target.value })
              }
              placeholder="Address line 2 (optional)"
              autoComplete="address-line2"
            />
          </EditableSubfield>
          <EditableSubfield label="City">
            <Input
              variant="underline"
              value={addressField.draft.city}
              onChange={(e) =>
                addressField.setDraft({ ...addressField.draft, city: e.target.value })
              }
              placeholder="City"
              autoComplete="address-level2"
            />
          </EditableSubfield>
          {isUs ? (
            <EditableSubfield label="State">
              <Select
                items={STATE_OPTIONS}
                value={addressField.draft.state}
                onValueChange={(next) =>
                  addressField.setDraft({ ...addressField.draft, state: next })
                }
              >
                <SelectTrigger variant="underline">
                  <SelectValue placeholder="State" />
                </SelectTrigger>
                <SelectContent>
                  {STATE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </EditableSubfield>
          ) : null}
          <EditableSubfield label={postalCodeLabel}>
            <Input
              variant="underline"
              value={addressField.draft.postalCode}
              onChange={(e) =>
                addressField.setDraft({ ...addressField.draft, postalCode: e.target.value })
              }
              placeholder={postalCodeLabel}
              autoComplete="postal-code"
            />
          </EditableSubfield>
          <EditableSubfield label="Country">
            <Input
              variant="underline"
              value={addressField.draft.country}
              onChange={(e) =>
                addressField.setDraft({ ...addressField.draft, country: e.target.value })
              }
              placeholder="Country"
              autoComplete="country-name"
            />
          </EditableSubfield>
        </div>
      ) : (
        <EditableValue>{displayValue}</EditableValue>
      )}
      <EditActions field={addressField} />
    </EditableField>
  )
}

/**
 * Joins the populated address fields into a single comma-separated
 * line for read-mode display. Returns `null` if the address is
 * entirely empty so callers can render a "Not set" placeholder.
 */
function formatAddress(
  member: {
    address1: string | null
    address2: string | null
    city: string | null
    state: string | null
    postalCode: string | null
    country: string | null
  },
  isUs: boolean
): string | null {
  const parts = [
    member.address1,
    member.address2,
    member.city,
    isUs ? member.state : null,
    member.postalCode,
    member.country,
  ].filter((p): p is string => Boolean(p?.trim()))
  return parts.length > 0 ? parts.join(', ') : null
}
